import {
  type Attendee,
  type CalendarConnection,
  type CalendarError,
  CalendarErrorType,
  type CalendarEvent,
  type CalendarProvider,
  type RecurrencePattern,
} from '../types/calendar';
import { calendarErrorHandler } from './calendarErrorHandler';

interface MicrosoftCalendarEvent {
  id?: string;
  subject?: string;
  bodyPreview?: string;
  body?: { content?: string; contentType?: string };
  location?: { displayName?: string };
  webLink?: string;
  start?: { dateTime?: string; date?: string; timeZone?: string };
  end?: { dateTime?: string; date?: string; timeZone?: string };
  attendees?: MicrosoftAttendee[];
  recurrence?: MicrosoftRecurrence;
  onlineMeeting?: { joinUrl?: string };
  isAllDay?: boolean;
  showAs?: string;
}

interface MicrosoftAttendee {
  emailAddress?: { address?: string; name?: string };
  status?: { response?: string };
}

interface MicrosoftRecurrence {
  pattern?: {
    type?: string;
    interval?: number;
    daysOfWeek?: string[];
  };
  range?: {
    endDate?: string;
    numberOfOccurrences?: number;
  };
}

export interface MicrosoftCalendarConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  scopes: string[];
  tenantId?: string;
}

export interface MicrosoftTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
}

export interface MicrosoftCalendarAuthResult {
  success: boolean;
  connection?: CalendarConnection;
  error?: CalendarError;
}

export class MicrosoftCalendarService {
  private static readonly MICROSOFT_AUTH_URL = 'https://login.microsoftonline.com';
  private static readonly MICROSOFT_TOKEN_URL = 'https://login.microsoftonline.com';
  private static readonly MICROSOFT_GRAPH_API_URL = 'https://graph.microsoft.com/v1.0';

  private config: MicrosoftCalendarConfig;

  constructor(config: MicrosoftCalendarConfig) {
    this.config = {
      ...config,
      // `common` allows work/school and personal MSA (outlook.com, live.com, etc.).
      // `organizations` or a single tenant GUID blocks personal Microsoft accounts.
      tenantId: config.tenantId || 'common',
      scopes: config.scopes || [
        'https://graph.microsoft.com/calendars.read',
        'https://graph.microsoft.com/calendars.readwrite',
      ],
    };
  }

  /**
   * Generate Microsoft Graph OAuth URL with calendar scopes for user authorization
   * Requirement 6.1: Initiate Microsoft calendar OAuth flow
   */
  public initiateAuth(state?: string): string {
    const params = new URLSearchParams({
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      response_type: 'code',
      scope: this.config.scopes.join(' '),
      response_mode: 'query',
      ...(state && { state }),
    });

    const authUrl = `${MicrosoftCalendarService.MICROSOFT_AUTH_URL}/${this.config.tenantId}/oauth2/v2.0/authorize`;
    return `${authUrl}?${params.toString()}`;
  }

  /**
   * Exchange authorization code for access and refresh tokens
   * Requirement 6.2: Handle OAuth callback and token exchange
   */
  public async handleCallback(
    authorizationCode: string,
    userId: string,
  ): Promise<MicrosoftCalendarAuthResult> {
    try {
      return await calendarErrorHandler
        .handleConnectionError(
          'microsoft',
          async () => {
            const tokenResponse = await this.exchangeCodeForTokens(authorizationCode);

            if (!(tokenResponse.success && tokenResponse.tokens)) {
              throw calendarErrorHandler.createError(
                CalendarErrorType.AUTH_FAILED,
                'Failed to exchange authorization code for tokens',
                true,
                'microsoft',
              );
            }

            const connection = await this.createConnection(userId, tokenResponse.tokens);

            // Store connection securely
            await this.storeConnection(connection);

            return connection;
          },
          userId,
        )
        .then((connection) => ({
          success: true,
          connection,
        }));
    } catch (error) {
      console.error('[MicrosoftCalendarService] OAuth callback error:', error);

      const calendarError =
        error instanceof Error && 'type' in error
          ? (error as unknown as CalendarError)
          : calendarErrorHandler.createError(
              CalendarErrorType.AUTH_FAILED,
              error instanceof Error ? error.message : 'Unknown authentication error',
              true,
              'microsoft',
            );

      return {
        success: false,
        error: calendarError,
      };
    }
  }

  /**
   * Exchange authorization code for tokens
   * Private method to handle token exchange with Microsoft Graph
   */
  private async exchangeCodeForTokens(code: string): Promise<{
    success: boolean;
    tokens?: MicrosoftTokenResponse;
    error?: string;
  }> {
    try {
      const tokenUrl = `${MicrosoftCalendarService.MICROSOFT_TOKEN_URL}/${this.config.tenantId}/oauth2/v2.0/token`;

      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: this.config.clientId,
          client_secret: this.config.clientSecret,
          code,
          grant_type: 'authorization_code',
          redirect_uri: this.config.redirectUri,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          '[MicrosoftCalendarService] Token exchange failed:',
          response.status,
          errorText,
        );
        return {
          success: false,
          error: `Token exchange failed: ${response.status}`,
        };
      }

      const tokens: MicrosoftTokenResponse = await response.json();
      return {
        success: true,
        tokens,
      };
    } catch (error) {
      console.error('[MicrosoftCalendarService] Token exchange error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown token exchange error',
      };
    }
  }

  /**
   * Create calendar connection object from tokens
   * Private method to structure connection data
   */
  private async createConnection(
    userId: string,
    tokens: MicrosoftTokenResponse,
  ): Promise<CalendarConnection> {
    const provider: CalendarProvider = {
      id: 'microsoft',
      name: 'Microsoft Calendar',
      iconPath: '/static/assets/icons/landing-page/outlook.svg',
      authUrl: `${MicrosoftCalendarService.MICROSOFT_AUTH_URL}/${this.config.tenantId}/oauth2/v2.0/authorize`,
      scopes: this.config.scopes,
    };

    const expiresAt = new Date();
    expiresAt.setSeconds(expiresAt.getSeconds() + tokens.expires_in);

    return {
      id: this.generateConnectionId(),
      userId,
      provider,
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      expiresAt,
      createdAt: new Date(),
      lastSyncAt: undefined,
    };
  }

  /**
   * Store calendar connection securely
   * Requirement 6.3: Secure token storage and connection persistence
   */
  private async storeConnection(connection: CalendarConnection): Promise<void> {
    try {
      // Store in localStorage for now - in production this should be encrypted and stored securely
      const storageKey = `calendar_connection_${connection.provider.id}_${connection.userId}`;
      const connectionData = {
        ...connection,
        // Don't store sensitive tokens in localStorage in production
        accessToken: this.encryptToken(connection.accessToken),
        refreshToken: this.encryptToken(connection.refreshToken),
      };

      localStorage.setItem(storageKey, JSON.stringify(connectionData));

      // Also store a reference for quick lookup
      const connectionsKey = `calendar_connections_${connection.userId}`;
      const existingConnections = this.getStoredConnections(connection.userId);
      const updatedConnections = existingConnections.filter(
        (conn) => conn.provider.id !== connection.provider.id,
      );
      updatedConnections.push(connection);

      localStorage.setItem(connectionsKey, JSON.stringify(updatedConnections));

      console.log('[MicrosoftCalendarService] Connection stored successfully');
    } catch (error) {
      console.error('[MicrosoftCalendarService] Failed to store connection:', error);
      throw new Error('Failed to store calendar connection');
    }
  }

  /**
   * Retrieve stored calendar connections for a user
   * Requirement 6.4: Connection persistence
   */
  public getStoredConnections(userId: string): CalendarConnection[] {
    try {
      const connectionsKey = `calendar_connections_${userId}`;
      const stored = localStorage.getItem(connectionsKey);

      if (!stored) {
        return [];
      }

      const connections: CalendarConnection[] = JSON.parse(stored);
      return connections.map((conn) => ({
        ...conn,
        // Decrypt tokens when retrieving
        accessToken: this.decryptToken(conn.accessToken),
        refreshToken: this.decryptToken(conn.refreshToken),
        expiresAt: new Date(conn.expiresAt),
        createdAt: new Date(conn.createdAt),
        lastSyncAt: conn.lastSyncAt ? new Date(conn.lastSyncAt) : undefined,
      }));
    } catch (error) {
      console.error('[MicrosoftCalendarService] Failed to retrieve connections:', error);
      return [];
    }
  }

  /**
   * Get Microsoft Calendar connection for a user
   */
  public getMicrosoftConnection(userId: string): CalendarConnection | null {
    const connections = this.getStoredConnections(userId);
    return connections.find((conn) => conn.provider.id === 'microsoft') || null;
  }

  /**
   * Refresh expired access token using refresh token
   * Requirement 6.4: Token refresh for connection persistence
   */
  public async refreshAccessToken(
    connection: CalendarConnection,
  ): Promise<CalendarConnection | null> {
    try {
      return await calendarErrorHandler.handleTokenRefresh(connection, connection.userId);
    } catch (error) {
      console.error('[MicrosoftCalendarService] Token refresh failed:', error);
      return null;
    }
  }

  /**
   * Internal method to refresh token (used by error handler)
   */
  public async _refreshTokenInternal(connection: CalendarConnection): Promise<CalendarConnection> {
    const tokenUrl = `${MicrosoftCalendarService.MICROSOFT_TOKEN_URL}/${this.config.tenantId}/oauth2/v2.0/token`;

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: this.config.clientId,
        client_secret: this.config.clientSecret,
        refresh_token: connection.refreshToken,
        grant_type: 'refresh_token',
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[MicrosoftCalendarService] Token refresh failed:', response.status, errorText);
      throw calendarErrorHandler.createError(
        CalendarErrorType.TOKEN_EXPIRED,
        `Token refresh failed: ${response.status}`,
        response.status >= 500, // Retry server errors
        'microsoft',
      );
    }

    const tokens: Partial<MicrosoftTokenResponse> = await response.json();

    if (!tokens.access_token) {
      throw calendarErrorHandler.createError(
        CalendarErrorType.AUTH_FAILED,
        'Token refresh response missing access_token',
        false,
        'microsoft',
      );
    }

    const updatedConnection: CalendarConnection = {
      ...connection,
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token || connection.refreshToken,
      expiresAt: new Date(Date.now() + (tokens.expires_in ?? 3600) * 1000),
    };

    // Store updated connection
    await this.storeConnection(updatedConnection);

    return updatedConnection;
  }

  /**
   * Remove stored calendar connection
   */
  public async disconnectCalendar(userId: string): Promise<void> {
    try {
      const storageKey = `calendar_connection_microsoft_${userId}`;
      localStorage.removeItem(storageKey);

      // Update connections list
      const connectionsKey = `calendar_connections_${userId}`;
      const connections = this.getStoredConnections(userId);
      const filteredConnections = connections.filter((conn) => conn.provider.id !== 'microsoft');

      if (filteredConnections.length > 0) {
        localStorage.setItem(connectionsKey, JSON.stringify(filteredConnections));
      } else {
        localStorage.removeItem(connectionsKey);
      }

      console.log('[MicrosoftCalendarService] Calendar disconnected successfully');
    } catch (error) {
      console.error('[MicrosoftCalendarService] Failed to disconnect calendar:', error);
      throw new Error('Failed to disconnect calendar');
    }
  }

  /**
   * Basic token encryption (in production, use proper encryption)
   */
  private encryptToken(token: string): string {
    // Simple base64 encoding for demo - use proper encryption in production
    return btoa(token);
  }

  /**
   * Basic token decryption (in production, use proper decryption)
   */
  private decryptToken(encryptedToken: string): string {
    // Simple base64 decoding for demo - use proper decryption in production
    try {
      return atob(encryptedToken);
    } catch {
      return encryptedToken; // Return as-is if not encrypted
    }
  }

  /**
   * Generate unique connection ID
   */
  private generateConnectionId(): string {
    return `microsoft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Validate if connection is still valid (not expired)
   * Includes 5-minute buffer for proactive token refresh
   */
  public isConnectionValid(connection: CalendarConnection): boolean {
    const now = new Date();
    // Ensure expiresAt is a Date object
    const expiresAt =
      connection.expiresAt instanceof Date ? connection.expiresAt : new Date(connection.expiresAt);

    // Add 5-minute buffer - refresh tokens 5 minutes before expiration
    const bufferMs = 5 * 60 * 1000;
    return now.getTime() < expiresAt.getTime() - bufferMs;
  }

  /**
   * Get valid connection, refreshing token if necessary
   */
  public async getValidConnection(userId: string): Promise<CalendarConnection | null> {
    const connection = this.getMicrosoftConnection(userId);

    if (!connection) {
      return null;
    }

    if (this.isConnectionValid(connection)) {
      return connection;
    }

    // Token expired, try to refresh
    return await this.refreshAccessToken(connection);
  }

  /**
   * Sync events from Microsoft Calendar
   * Requirement 8.4, 8.5, 8.6: Event synchronization and caching
   */
  public async syncEvents(userId: string): Promise<CalendarEvent[]> {
    return await calendarErrorHandler.handleSyncError(
      'microsoft',
      async () => {
        const connection = await this.getValidConnection(userId);

        if (!connection) {
          throw calendarErrorHandler.createError(
            CalendarErrorType.AUTH_FAILED,
            'No valid Microsoft Calendar connection found',
            false,
            'microsoft',
          );
        }

        const events = await this.fetchEventsFromMicrosoft(connection);

        // Cache events locally
        await this.cacheEvents(userId, events);

        // Update last sync time
        const updatedConnection = {
          ...connection,
          lastSyncAt: new Date(),
        };
        await this.storeConnection(updatedConnection);

        console.log(`[MicrosoftCalendarService] Synced ${events.length} events for user ${userId}`);
        return events;
      },
      async () => {
        // Fallback to cached events
        const cachedEvents = await this.getCachedEvents(userId);
        console.log(
          `[MicrosoftCalendarService] Returning ${cachedEvents.length} cached events as fallback`,
        );
        return cachedEvents;
      },
      userId,
    );
  }

  /**
   * Fetch events from Microsoft Graph API
   */
  private async fetchEventsFromMicrosoft(connection: CalendarConnection): Promise<CalendarEvent[]> {
    try {
      // Get events for the next 30 days
      const startTime = new Date().toISOString();
      const endTime = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      const params = new URLSearchParams({
        startDateTime: startTime,
        endDateTime: endTime,
        $orderby: 'start/dateTime',
        $top: '250',
        $select: 'id,subject,start,end,attendees,webLink,location,body,recurrence,showAs',
      });

      const url = `${MicrosoftCalendarService.MICROSOFT_GRAPH_API_URL}/me/events?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${connection.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          '[MicrosoftCalendarService] Events fetch failed:',
          response.status,
          errorText,
        );
        throw new Error(`Failed to fetch events: ${response.status}`);
      }

      const data = await response.json();
      return this.transformMicrosoftEvents(data.value || []);
    } catch (error) {
      console.error('[MicrosoftCalendarService] Error fetching events:', error);
      throw error;
    }
  }

  /**
   * Transform Microsoft Graph events to internal format
   */
  private transformMicrosoftEvents(microsoftEvents: MicrosoftCalendarEvent[]): CalendarEvent[] {
    return microsoftEvents.map((event) => {
      const startTime = new Date(event.start?.dateTime || event.start?.date || 0);
      const endTime = new Date(event.end?.dateTime || event.end?.date || 0);
      const now = new Date();

      let status: 'upcoming' | 'in-progress' | 'completed' = 'upcoming';
      if (now >= startTime && now <= endTime) {
        status = 'in-progress';
      } else if (now > endTime) {
        status = 'completed';
      }

      const attendees: Attendee[] = (event.attendees || []).map((attendee: MicrosoftAttendee) => ({
        id: attendee.emailAddress?.address || attendee.emailAddress?.name || '',
        name: attendee.emailAddress?.name || attendee.emailAddress?.address || '',
        email: attendee.emailAddress?.address || '',
        avatar: undefined,
        status: this.mapMicrosoftAttendeeStatus(attendee.status?.response),
      }));

      return {
        id: event.id || '',
        title: event.subject || 'Untitled Event',
        startTime,
        endTime,
        attendees,
        meetingUrl: this.extractMeetingUrl(event),
        location: event.location?.displayName,
        description: event.body?.content,
        recurrence: this.parseMicrosoftRecurrence(event.recurrence),
        status,
      };
    });
  }

  /**
   * Map Microsoft attendee status to internal format
   */
  private mapMicrosoftAttendeeStatus(
    microsoftStatus?: string,
  ): 'accepted' | 'declined' | 'tentative' | 'pending' {
    switch (microsoftStatus) {
      case 'accepted':
        return 'accepted';
      case 'declined':
        return 'declined';
      case 'tentative':
        return 'tentative';
      default:
        return 'pending';
    }
  }

  /**
   * Extract meeting URL from Microsoft Calendar event
   */
  private extractMeetingUrl(event: MicrosoftCalendarEvent): string | undefined {
    // Check for Teams meeting link
    if (event.onlineMeeting?.joinUrl) {
      return event.onlineMeeting.joinUrl;
    }

    // Check webLink
    if (event.webLink) {
      return event.webLink;
    }

    // Check for other meeting URLs in body or location
    const text = `${event.body?.content || ''} ${event.location?.displayName || ''}`;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = text.match(urlRegex);

    if (urls) {
      // Look for common meeting platforms
      const meetingUrl = urls.find(
        (url) =>
          url.includes('teams.microsoft.com') ||
          url.includes('zoom.us') ||
          url.includes('meet.google.com') ||
          url.includes('webex.com') ||
          url.includes('gotomeeting.com'),
      );
      return meetingUrl;
    }

    return undefined;
  }

  /**
   * Parse Microsoft Calendar recurrence rules (simplified)
   */
  private parseMicrosoftRecurrence(
    recurrence?: MicrosoftRecurrence,
  ): RecurrencePattern | undefined {
    if (!recurrence?.pattern) {
      return undefined;
    }

    const pattern = recurrence.pattern;
    let frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';

    switch (pattern.type) {
      case 'daily':
        frequency = 'daily';
        break;
      case 'weekly':
        frequency = 'weekly';
        break;
      case 'absoluteMonthly':
      case 'relativeMonthly':
        frequency = 'monthly';
        break;
      case 'absoluteYearly':
      case 'relativeYearly':
        frequency = 'yearly';
        break;
      default:
        return undefined;
    }

    return {
      frequency,
      interval: pattern.interval || 1,
      endDate: recurrence.range?.endDate ? new Date(recurrence.range.endDate) : undefined,
      occurrences: recurrence.range?.numberOfOccurrences,
    };
  }

  /**
   * Cache events in local storage
   */
  private async cacheEvents(userId: string, events: CalendarEvent[]): Promise<void> {
    try {
      const cacheKey = `calendar_events_microsoft_${userId}`;
      const cacheData = {
        events,
        cachedAt: new Date().toISOString(),
        ttl: 15 * 60 * 1000, // 15 minutes TTL
      };

      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      console.log(`[MicrosoftCalendarService] Cached ${events.length} events for user ${userId}`);
    } catch (error) {
      console.error('[MicrosoftCalendarService] Failed to cache events:', error);
    }
  }

  /**
   * Get cached events from local storage
   */
  public async getCachedEvents(userId: string): Promise<CalendarEvent[]> {
    try {
      const cacheKey = `calendar_events_microsoft_${userId}`;
      const cached = localStorage.getItem(cacheKey);

      if (!cached) {
        return [];
      }

      const cacheData = JSON.parse(cached);
      const cachedAt = new Date(cacheData.cachedAt);
      const now = new Date();

      // Check if cache is still valid (within TTL)
      if (now.getTime() - cachedAt.getTime() > cacheData.ttl) {
        console.log('[MicrosoftCalendarService] Cache expired, removing stale data');
        localStorage.removeItem(cacheKey);
        return [];
      }

      // Transform cached events back to proper Date objects
      const events = cacheData.events.map((event: Record<string, unknown>) => ({
        ...event,
        startTime: new Date(event.startTime as string),
        endTime: new Date(event.endTime as string),
      }));

      return events;
    } catch (error) {
      console.error('[MicrosoftCalendarService] Failed to get cached events:', error);
      return [];
    }
  }

  /**
   * Clear cached events for a user
   */
  public async clearCachedEvents(userId: string): Promise<void> {
    try {
      const cacheKey = `calendar_events_microsoft_${userId}`;
      localStorage.removeItem(cacheKey);
      console.log(`[MicrosoftCalendarService] Cleared cached events for user ${userId}`);
    } catch (error) {
      console.error('[MicrosoftCalendarService] Failed to clear cached events:', error);
    }
  }
}

// Export singleton instance with default configuration
// In production, these should come from environment variables
const env = (import.meta.env ?? {}) as Record<string, string | undefined>;
export const microsoftCalendarService = new MicrosoftCalendarService({
  clientId: env.VITE_MICROSOFT_CLIENT_ID || '',
  clientSecret: env.VITE_MICROSOFT_CLIENT_SECRET || '',
  redirectUri:
    env.VITE_MICROSOFT_REDIRECT_URI || `${window.location.origin}/auth/microsoft/callback`,
  tenantId: env.VITE_MICROSOFT_TENANT_ID || 'common',
  scopes: [
    'https://graph.microsoft.com/calendars.read',
    'https://graph.microsoft.com/calendars.readwrite',
  ],
});
